<script setup lang="ts">
const verificationSteps = [
  { name: "Personal Details", status: "complete" },
  { name: "Profile Photo", status: "complete" },
  { name: "Experience", status: "complete" },
  { name: "Video Bio", status: "pending" },
];
</script>

<template>
  <div class="relative">
    <!-- Main Verification Card -->
    <div class="rounded-3xl 3xl:rounded-[2rem] overflow-hidden shadow-warm-lg bg-white border border-sand/50">
      <!-- Header -->
      <div class="bg-gradient-to-r from-primary to-primary-light p-3 sm:p-5 2xl:p-6 3xl:p-8 4xl:p-10">
        <div class="flex items-center gap-2 sm:gap-3 2xl:gap-4">
          <div
            class="w-9 h-9 sm:w-12 sm:h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0"
          >
            <Icon
              name="material-symbols:verified"
              class="w-5 h-5 sm:w-6 sm:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 text-white"
            />
          </div>
          <div>
            <span class="text-white font-semibold text-sm sm:text-base 2xl:text-lg 3xl:text-xl 4xl:text-2xl block"
              >Profile Verification</span
            >
            <span class="text-white/80 text-xs sm:text-sm 2xl:text-base 3xl:text-lg">Stand out from the crowd</span>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="p-4 sm:p-6 2xl:p-8 3xl:p-10 4xl:p-12 space-y-4 sm:space-y-6 2xl:space-y-8 3xl:space-y-10">
        <!-- Status Card -->
        <div class="bg-cream rounded-xl sm:rounded-2xl 3xl:rounded-3xl p-4 sm:p-5 2xl:p-6 3xl:p-8 text-center">
          <div
            class="w-12 h-12 sm:w-16 sm:h-16 2xl:w-20 2xl:h-20 3xl:w-24 3xl:h-24 4xl:w-28 4xl:h-28 mx-auto mb-3 sm:mb-4 2xl:mb-5 rounded-full bg-gradient-to-br from-secondary/20 to-savanna/20 flex items-center justify-center"
          >
            <Icon
              name="material-symbols:schedule"
              class="w-6 h-6 sm:w-8 sm:h-8 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 4xl:w-14 4xl:h-14 text-secondary"
            />
          </div>
          <h4 class="text-base sm:text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl font-bold text-dark-gray mb-1 sm:mb-2">Under Review</h4>
          <p class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg text-medium-gray">Our team is checking your profile</p>
        </div>

        <!-- Checklist -->
        <div class="space-y-2 sm:space-y-3 2xl:space-y-4 3xl:space-y-5">
          <div
            v-for="step in verificationSteps"
            :key="step.name"
            class="flex items-center gap-2 sm:gap-3 2xl:gap-4 p-2 sm:p-3 2xl:p-4 3xl:p-5 rounded-lg sm:rounded-xl 2xl:rounded-2xl"
            :class="step.status === 'complete' ? 'bg-savanna/5' : 'bg-secondary/5'"
          >
            <div
              class="w-6 h-6 sm:w-8 sm:h-8 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 4xl:w-14 4xl:h-14 rounded-full flex items-center justify-center flex-shrink-0"
              :class="step.status === 'complete' ? 'bg-savanna' : 'bg-secondary'"
            >
              <Icon
                :name="step.status === 'complete' ? 'material-symbols:check' : 'material-symbols:hourglass-empty'"
                class="w-3 h-3 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7 text-white"
              />
            </div>
            <span
              class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl font-medium"
              :class="step.status === 'complete' ? 'text-dark-gray' : 'text-medium-gray'"
            >
              {{ step.name }}
            </span>
          </div>
        </div>

        <!-- Progress Bar -->
        <div>
          <div class="flex items-center justify-between mb-1.5 sm:mb-2 2xl:mb-3">
            <span class="text-[10px] sm:text-xs 2xl:text-sm 3xl:text-base text-medium-gray">Verification Progress</span>
            <span class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg font-bold text-savanna">75%</span>
          </div>
          <div class="h-1.5 sm:h-2 2xl:h-3 3xl:h-4 bg-sand rounded-full overflow-hidden">
            <div class="h-full w-[75%] bg-gradient-to-r from-secondary to-savanna rounded-full"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Badge Card -->
    <div
      class="hidden lg:flex absolute -right-4 lg:-right-8 2xl:-right-12 3xl:-right-16 top-1/3 z-20 bg-white rounded-2xl 3xl:rounded-3xl shadow-warm-lg p-4 2xl:p-5 3xl:p-6 animate-gentle-float opacity-100"
      style="animation-delay: 0.3s"
    >
      <div class="flex items-center gap-3 2xl:gap-4">
        <div
          class="w-12 h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 rounded-xl 2xl:rounded-2xl bg-gradient-to-br from-savanna to-savanna-light flex items-center justify-center"
        >
          <Icon name="material-symbols:verified" class="w-6 h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 text-white" />
        </div>
        <div>
          <p class="text-xs 2xl:text-sm 3xl:text-base text-medium-gray">Get the</p>
          <p class="text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl font-bold text-dark-gray">Verified Badge</p>
        </div>
      </div>
    </div>

    <!-- Floating Time Badge -->
    <div
      class="hidden lg:flex absolute -left-4 lg:-left-8 2xl:-left-12 bottom-1/4 z-20 bg-gradient-to-r from-gold to-gold-light rounded-full shadow-gold px-4 py-2 2xl:px-6 2xl:py-3 3xl:px-8 3xl:py-4 4xl:px-10 4xl:py-5 animate-gentle-float"
      style="animation-delay: 0.6s"
    >
      <div class="flex items-center gap-2 2xl:gap-3">
        <Icon name="material-symbols:timer" class="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
        <span class="text-sm 2xl:text-base 3xl:text-lg font-semibold text-white">~24 hours</span>
      </div>
    </div>

    <!-- Decorative blur -->
    <div class="absolute -z-10 -top-8 -right-8 w-48 h-48 2xl:w-64 2xl:h-64 3xl:w-80 3xl:h-80 bg-savanna/10 rounded-full blur-3xl"></div>
  </div>
</template>
